import { useEffect, useState } from 'react';

/**
 * Normalised 0-1 loudness for a live microphone track.
 *
 * This exists so the pre-join screen can answer the one question a device
 * picker cannot: "is this the microphone that can actually hear me?" A list of
 * device names never catches a muted headset or the wrong input.
 */
export function useAudioLevel(track: MediaStreamTrack | null, active: boolean): number {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    if (!track || !active) {
      setLevel(0);
      return;
    }

    const AudioContextCtor = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioContextCtor) return;

    const context = new AudioContextCtor();
    const source = context.createMediaStreamSource(new MediaStream([track]));
    const analyser = context.createAnalyser();
    // Small FFT: we want a responsive envelope, not a spectrum.
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.75;
    source.connect(analyser);

    const samples = new Uint8Array(analyser.frequencyBinCount);
    let frame = 0;

    const tick = () => {
      analyser.getByteTimeDomainData(samples);
      // RMS around the 128 midpoint of unsigned 8-bit PCM.
      let sum = 0;
      for (const sample of samples) {
        const centred = (sample - 128) / 128;
        sum += centred * centred;
      }
      const rms = Math.sqrt(sum / samples.length);
      // Speech sits low in a linear scale; the curve lifts it into a range
      // where the meter visibly moves at a normal talking volume.
      setLevel(Math.min(1, rms * 3.2));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      analyser.disconnect();
      void context.close().catch(() => undefined);
    };
  }, [track, active]);

  return level;
}
